import { type FC, memo, useLayoutEffect, useRef, useState } from 'react';
import {
  type EdgeProps,
  type Node,
  useNodes,
  getSmoothStepPath,
  Position,
} from 'reactflow';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import {
  getLastPlacingMergeStep,
  getLastPlacingStep,
  getMergeAddNodesWhileMoving,
  getMergeStepsWithChidren,
  getPlacing,
  getTrailingBranchToHide,
} from 'pages/AutomationEngine/store/selectors';
import { fontWeights } from 'app/typography';
import { CARD_EDGE_BEND_OFFSET, EDGE_BORDER_RADIUS } from './conatants';

const LABEL_PADDING = { x: 4, y: 2 };
// Short labels still get a background of at least this length
const LABEL_MIN_WIDTH = 20;

const isLeaf = (id: string, nodes: Node[]) => {
  const node = nodes.find((n) => n.id === id);
  return Boolean(node && node.data?.leaf);
};

// React-flow's getSmoothStepPath() collapses the first corner when the bend sits exactly at its
// source offset, so for this (most common) case the path is built here.
const getStepPath = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  bendX,
  bendAtTarget,
}: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  bendX: number;
  bendAtTarget?: boolean;
}) => {
  const dy = targetY - sourceY;

  // When the source and target are horizontally aligned, just draw a straight line
  if (dy === 0) {
    return `M${sourceX} ${sourceY}L${targetX} ${targetY}`;
  }

  if (Math.abs(dy) < 2 * EDGE_BORDER_RADIUS) {
    // Too short for two full corners, so draw a single S-curve instead.
    // Shift the bend toward the source, or toward the target card when bending at it
    bendX += bendAtTarget ? 10 : -10;
    const curveWidth = Math.max(
      0,
      Math.min(
        EDGE_BORDER_RADIUS + CARD_EDGE_BEND_OFFSET,
        bendX - sourceX,
        targetX - bendX
      )
    );
    return [
      `M${sourceX} ${sourceY}`,
      `L${bendX - curveWidth} ${sourceY}`,
      `C${bendX} ${sourceY} ${bendX} ${targetY} ${bendX + curveWidth} ${targetY}`,
      `L${targetX} ${targetY}`,
    ].join('');
  }

  const dir = dy > 0 ? 1 : -1;

  const sourceRadius = Math.min(
    EDGE_BORDER_RADIUS,
    bendX - sourceX,
    Math.abs(dy) / 2
  );

  const targetRadius = Math.min(
    EDGE_BORDER_RADIUS,
    targetX - bendX,
    Math.abs(dy) / 2
  );

  return [
    `M${sourceX} ${sourceY}`,
    `L${bendX - sourceRadius} ${sourceY}`,
    `Q${bendX} ${sourceY} ${bendX} ${sourceY + dir * sourceRadius}`,
    `L${bendX} ${targetY - dir * targetRadius}`,
    `Q${bendX} ${targetY} ${bendX + targetRadius} ${targetY}`,
    `L${targetX} ${targetY}`,
  ].join('');
};

// Vertical label centered on (x, y). Text and background share a single origin and
// are rotated together, so the label is centered on the edge regardless of its length.
const EdgeLabel: FC<{
  x: number;
  y: number;
  label: string;
  color?: string;
}> = ({ x, y, label, color }) => {
  const textRef = useRef<SVGTextElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    let cancelled = false;
    const measure = () => {
      if (!cancelled && textRef.current) {
        const { width, height } = textRef.current.getBBox();
        setSize({ width, height });
      }
    };
    measure();
    // Re-measure once web fonts are in, the fallback font has different metrics
    document.fonts?.ready.then(measure);
    return () => {
      cancelled = true;
    };
  }, [label]);

  const width = Math.max(
    (size.width ?? 0) + 2 * LABEL_PADDING.x,
    LABEL_MIN_WIDTH
  );
  const height = (size.height ?? 0) + 2 * LABEL_PADDING.y;

  return (
    <g
      className="react-flow__edge-textwrapper"
      transform={`translate(${x} ${y}) rotate(-90)`}
      visibility={size.width && size.height ? 'visible' : 'hidden'}
    >
      <rect
        className="react-flow__edge-textbg"
        x={-width / 2}
        y={-height / 2}
        width={width}
        height={height}
        rx={2}
        ry={2}
        style={{ fill: color || 'var(--color-button-primary-hover)' }}
      />
      <text
        ref={textRef}
        className="react-flow__edge-text"
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fill: 'var(--color-background-white)',
          fontWeight: fontWeights.bold,
          textTransform: 'uppercase',
          fontSize: '.7em', // Just a one-off size, not part of the style guide
        }}
      >
        {label}
      </text>
    </g>
  );
};

// This is disabled because effectively this is a passthrough to react-flow's
// getSmoothStepPath() internals, and this component is rendered by react-flow.
export const BasicEdgeCore: FC<EdgeProps> = memo(
  ({
    sourceX,
    sourceY,
    targetX,
    targetY,
    style,
    sourcePosition,
    targetPosition,
    label,
    data,
    markerEnd,
    markerStart,
  }) => {
    const offset = EDGE_BORDER_RADIUS / 2;
    const sourceDir = sourceX < targetX ? 1 : -1;
    // Edges bend one corner radius away from the source handle (or before the target
    // handle when flagged), leaving room for a full rounded corner
    const bendOffset =
      data?.bendOffset ?? EDGE_BORDER_RADIUS + CARD_EDGE_BEND_OFFSET;
    const centerX = data?.bendAtTarget
      ? targetX - bendOffset
      : sourceX + sourceDir * bendOffset;
    // Labels stay anchored 2 spacing units from the source handle, independent of
    // where the edge bends; the label transforms below are tuned to this anchor.
    const labelX = sourceX + sourceDir * offset;

    const path =
      sourcePosition === Position.Right &&
      targetPosition === Position.Left &&
      sourceX < centerX &&
      centerX < targetX
        ? getStepPath({
            sourceX,
            sourceY,
            targetX,
            targetY,
            bendX: centerX,
            bendAtTarget: data?.bendAtTarget && data?.bendOffset !== undefined,
          })
        : getSmoothStepPath({
            sourceX,
            centerX,
            sourceY,
            sourcePosition,
            targetX,
            targetY,
            targetPosition,
            borderRadius: EDGE_BORDER_RADIUS,
            offset,
          })[0];

    // In the react-flow source there is an example of UI for
    // for edge text, which we may want to reference.

    // Centered no further than 30px, even if the source and target are further apart
    const textCenterSign =
      (sourceY < targetY && 1) || (sourceY > targetY && -1) || 0;
    const textCenterY =
      sourceY + textCenterSign * Math.min(29, Math.abs(sourceY - targetY) / 2);

    return (
      <>
        <path
          style={style}
          className="react-flow__edge-path"
          d={path}
          markerEnd={markerEnd}
          markerStart={markerStart}
        />
        {label && typeof label === 'string' ? (
          <EdgeLabel
            x={labelX - (data?.isMerge ? EDGE_BORDER_RADIUS / 2 : 0)}
            y={textCenterY}
            label={label}
            color={data?.color}
          />
        ) : null}
      </>
    );
  }
);

export const BasicEdge: FC<EdgeProps> = ({ source, target, ...props }) => {
  const nodes = useNodes();

  const placing = useAutomationsSelector(getPlacing);
  const lastPlacingStep = useAutomationsSelector(getLastPlacingStep);
  const lastPlacingMergeStep = useAutomationsSelector(getLastPlacingMergeStep);
  const mergeStepsWithChidren = useAutomationsSelector(
    getMergeStepsWithChidren
  );
  const mergeAddNodesWhileMoving = useAutomationsSelector(
    getMergeAddNodesWhileMoving
  );
  const trailingBranchToHide = useAutomationsSelector(getTrailingBranchToHide);

  if (
    placing &&
    (((!lastPlacingStep.isBranchGroupInitiator ||
      (source === lastPlacingMergeStep && !mergeStepsWithChidren[source])) && // need to not show child
      !mergeAddNodesWhileMoving[target] &&
      source === lastPlacingStep.id) ||
      trailingBranchToHide?.includes(source)) &&
    isLeaf(target, nodes)
  ) {
    // When placing a step, we want to hide its following edge
    // if it points to leaf "add button" (the button will hide as well).
    return null;
  }
  return <BasicEdgeCore {...props} source={source} target={target} />;
};
